import React, { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Formik } from 'formik';
import { getGetPagesQueryKey, useCreatePage, useGetPages } from '@juno/client-api';
import { PageTypeEnum, SearchContentTypeEnum, Site } from '@juno/client-api/model';
import { GridV2 } from '@juno/modules';
import {
  Container,
  DefaultSearchSortFilter,
  DialogAriaWrapper,
  GenericFormikInput,
} from '@juno/ui';
import { slugify, usePagesSearchFilter } from '@juno/utils';

interface PagesAdminProps {
  site?: Site;
}
const PagesAdmin: React.FC<PagesAdminProps> = ({ site }) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [sort, setSort] = useState('title');
  const [debouncedSearch, search, searchFilter, setSearch] = usePagesSearchFilter();
  const { data: pages, isFetching: isLoading } = useGetPages(site?.id || '', {
    filter: { type: PageTypeEnum.page, ...searchFilter },
    order: sort,
  });

  const createPage = useCreatePage();
  const queryClient = useQueryClient();

  // Changed this over to Formik since formik handles onchange better
  const handleCreatePage = async (values: { name: string; slug: string }) => {
    setIsSaving(true);
    try {
      await createPage.mutateAsync({
        siteId: site?.id || '',
        data: {
          site: site?.id || '',
          title: values.name,
          slug: values.slug,
          type: PageTypeEnum.page,
          components: [],
        },
      });
      await queryClient.invalidateQueries({
        queryKey: getGetPagesQueryKey(site?.id || '', {
          filter: { type: PageTypeEnum.page },
        }),
      });
      setIsSaving(false);
      setCreateDialogOpen(false);
    } catch (e) {
      console.error(e);
      setIsSaving(false);
    }
  };

  const items = pages?.map((page) => ({
    title: page.title,
    slug: page.slug,
    abstract: `/${page.slug}`,
    components: page.components,
    id: page.id,
    icon: '',
  }));

  return (
    <Container>
      <Card sx={{ p: 2, mb: 4 }}>
        <Typography variant='h5'>Pages</Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant='body2' sx={{ mb: 6, maxWidth: 900 }}>
          These are pages around your site that can be edited and linked to. Clicking on one will
          take you to the page builder where you can add, edit, and rearrange sections to create
          engaging landing pages.
        </Typography>
        <DefaultSearchSortFilter
          buttonDisabled={false}
          buttonText={'New Page'}
          onClickButton={() => setCreateDialogOpen(true)}
          setSearch={(value: string) => setSearch(value)}
          setSort={setSort}
          sort={sort}
          showFilter={false}
          sortOptions={[
            { value: 'title', label: 'Alphabetical' },
            { value: 'slug', label: 'Page Slug' },
          ]}
        />
        <GridV2
          items={items || []}
          type={SearchContentTypeEnum.page}
          isLoading={isLoading}
          isAdmin={true}
        />

        <DialogAriaWrapper
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          id='create_new_page'
        >
          <Formik
            initialValues={{ name: '', slug: '' }}
            onSubmit={async (values) => {}}
            enableReinitialize
          >
            {({ values, setFieldValue }) => {
              return (
                <>
                  <Box>
                    <Typography variant='h6' sx={{ mt: 3, ml: 3, mb: 2 }}>
                      Create a new page
                    </Typography>
                  </Box>
                  <DialogContent>
                    <Box>
                      <GenericFormikInput
                        name='name'
                        label='Name'
                        value={values.name}
                        onChange={(e: any) => {
                          setFieldValue('name', e.target.value);
                          setFieldValue('slug', slugify(e.target.value));
                        }}
                      />
                      <GenericFormikInput name='slug' label='Slug' value={values.slug} />
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                    <LoadingButton
                      loading={isSaving}
                      onClick={() => handleCreatePage(values)}
                      variant='contained'
                    >
                      Create
                    </LoadingButton>
                  </DialogActions>
                </>
              );
            }}
          </Formik>
        </DialogAriaWrapper>
      </Card>
    </Container>
  );
};

export default PagesAdmin;
